import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { getJournalAuditLogs, clearJournalAuditLogs } from '../../../../redux/actions/SocietyPortal/JournalAction/JournalAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { formatIstDateTime } from '../../../../utils/helpers/istDate';

const ACTION_CONFIG = {
    CREATE: { icon: 'mdi:file-plus-outline', color: 'tw-text-emerald-500', bg: 'tw-bg-emerald-500', label: 'Created' },
    UPDATE: { icon: 'mdi:pencil-outline', color: 'tw-text-blue-500', bg: 'tw-bg-blue-500', label: 'Updated' },
    DELETE: { icon: 'mdi:trash-can-outline', color: 'tw-text-red-500', bg: 'tw-bg-red-500', label: 'Deleted' },
};

const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const sumAmounts = (lines) =>
    (lines || []).reduce((sum, l) => sum + (parseFloat(l.amount) || 0), 0);

const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return formatIstDateTime(d);
};

const ChangedFieldBadge = ({ field }) => (
    <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-medium tw-bg-amber-50 tw-text-amber-700 tw-border tw-border-amber-200">
        {field}
    </span>
);

const ValueDiff = ({ label, oldVal, newVal }) => {
    if (oldVal === newVal) return null;
    return (
        <div className="tw-flex tw-items-start tw-gap-2 tw-text-[10px] tw-py-1">
            <span className="tw-text-gray-500 tw-font-medium tw-min-w-[60px]">{label}:</span>
            <div className="tw-flex tw-items-center tw-gap-1.5">
                <span className="tw-line-through tw-text-red-400">{oldVal || '(empty)'}</span>
                <Icon icon="mdi:arrow-right" className="tw-text-gray-400 tw-text-xs" />
                <span className="tw-text-emerald-600 tw-font-medium">{newVal || '(empty)'}</span>
            </div>
        </div>
    );
};

const normalizeLines = (lines) =>
    (lines || []).map(l => ({ account_cd: l.account_cd, amount: parseFloat(l.amount) || 0 }));

const LinesCompare = ({ label, oldLines, newLines, type }) => {
    const color = type === 'debit' ? 'tw-text-emerald-600' : 'tw-text-red-600';
    const hasChanges = JSON.stringify(normalizeLines(oldLines)) !== JSON.stringify(normalizeLines(newLines));
    if (!hasChanges) return null;

    return (
        <div className="tw-mt-1.5">
            <p className="tw-text-[10px] tw-font-medium tw-text-gray-600 tw-mb-1">{label}:</p>
            <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                {/* Old */}
                <div className="tw-bg-red-50/50 tw-rounded tw-p-1.5 tw-border tw-border-red-100">
                    <p className="tw-text-[9px] tw-text-red-400 tw-font-medium tw-mb-1">Before</p>
                    {(oldLines || []).map((line, i) => (
                        <div key={i} className="tw-flex tw-justify-between tw-text-[10px] tw-py-0.5">
                            <span className="tw-text-gray-600 tw-truncate tw-mr-1">{line.account_cd}</span>
                            <span className="tw-text-red-500 tw-font-medium tw-whitespace-nowrap">₹{formatCurrency(line.amount)}</span>
                        </div>
                    ))}
                    {(!oldLines || oldLines.length === 0) && <p className="tw-text-[10px] tw-text-gray-400 tw-italic">None</p>}
                </div>
                {/* New */}
                <div className="tw-bg-emerald-50/50 tw-rounded tw-p-1.5 tw-border tw-border-emerald-100">
                    <p className="tw-text-[9px] tw-text-emerald-400 tw-font-medium tw-mb-1">After</p>
                    {(newLines || []).map((line, i) => (
                        <div key={i} className="tw-flex tw-justify-between tw-text-[10px] tw-py-0.5">
                            <span className="tw-text-gray-600 tw-truncate tw-mr-1">{line.account_cd}</span>
                            <span className={`${color} tw-font-medium tw-whitespace-nowrap`}>₹{formatCurrency(line.amount)}</span>
                        </div>
                    ))}
                    {(!newLines || newLines.length === 0) && <p className="tw-text-[10px] tw-text-gray-400 tw-italic">None</p>}
                </div>
            </div>
        </div>
    );
};

const AuditLogEntry = ({ log, isLast }) => {
    const config = ACTION_CONFIG[log.action] || ACTION_CONFIG.UPDATE;
    const badgeFields = log.metadata?.changed_fields || log.changed_fields || [];
    const oldJournal = log.old_values?.journal || {};
    const newJournal = log.new_values?.journal || {};

    const oldDebitTotal = sumAmounts(log.old_values?.debits);
    const newDebitTotal = sumAmounts(log.new_values?.debits);
    const oldCreditTotal = sumAmounts(log.old_values?.credits);
    const newCreditTotal = sumAmounts(log.new_values?.credits);

    // Directly compare journal fields to detect changes
    const journalDiffs = [];
    if (log.action === 'UPDATE' && log.old_values && log.new_values) {
        if (oldJournal.date !== newJournal.date) journalDiffs.push({ label: 'Date', oldVal: oldJournal.date, newVal: newJournal.date });
        if (oldJournal.remarks !== newJournal.remarks) journalDiffs.push({ label: 'Remarks', oldVal: oldJournal.remarks, newVal: newJournal.remarks });
        if (oldJournal.entry_type !== newJournal.entry_type) journalDiffs.push({ label: 'Type', oldVal: oldJournal.entry_type, newVal: newJournal.entry_type });
        if ((oldJournal.reff_no || '') !== (newJournal.reff_no || '')) journalDiffs.push({ label: 'Ref No', oldVal: oldJournal.reff_no, newVal: newJournal.reff_no });
        if ((oldJournal.cheque_no || '') !== (newJournal.cheque_no || '')) journalDiffs.push({ label: 'Cheque No', oldVal: oldJournal.cheque_no, newVal: newJournal.cheque_no });
        if ((oldJournal.clearance_date || '') !== (newJournal.clearance_date || '')) journalDiffs.push({ label: 'Clearance Date', oldVal: oldJournal.clearance_date, newVal: newJournal.clearance_date });
    }

    const hasDebitLineChanges = JSON.stringify(normalizeLines(log.old_values?.debits)) !== JSON.stringify(normalizeLines(log.new_values?.debits));
    const hasCreditLineChanges = JSON.stringify(normalizeLines(log.old_values?.credits)) !== JSON.stringify(normalizeLines(log.new_values?.credits));
    const hasAnyChanges = journalDiffs.length > 0 || oldDebitTotal !== newDebitTotal || oldCreditTotal !== newCreditTotal || hasDebitLineChanges || hasCreditLineChanges;

    return (
        <div className="tw-relative tw-pb-5">
            {/* Timeline dot */}
            <div className={`tw-absolute tw-left-[-16px] tw-top-1 tw-w-3 tw-h-3 tw-rounded-full ${config.bg} tw-border-2 tw-border-white tw-shadow-sm`}></div>
            {/* Timeline line */}
            {!isLast && <div className="tw-absolute tw-left-[-11px] tw-top-4 tw-w-px tw-h-full tw-bg-gray-200"></div>}

            <div>
                {/* Action header */}
                <div className="tw-flex tw-items-center tw-justify-between">
                    <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-flex tw-items-center tw-gap-1">
                        <Icon icon={config.icon} className={`${config.color} tw-text-sm`} />
                        Journal {config.label}
                    </p>
                    <span className="tw-text-[10px] tw-text-gray-400 tw-flex tw-items-center tw-gap-0.5">
                        <Icon icon="mdi:clock-outline" className="tw-text-[10px]" />
                        {formatDateTime(log.created_at)}
                    </span>
                </div>

                {/* Description */}
                {log.description && (
                    <p className="tw-text-[10px] tw-text-gray-500 tw-mt-0.5">{log.description}</p>
                )}

                {/* Changed fields badges */}
                {badgeFields.length > 0 && (
                    <div className="tw-flex tw-items-center tw-gap-1 tw-mt-1.5 tw-flex-wrap">
                        <span className="tw-text-[10px] tw-text-gray-400">Changed:</span>
                        {badgeFields.map((field) => (
                            <ChangedFieldBadge key={field} field={field} />
                        ))}
                    </div>
                )}

                {/* Value diffs for UPDATE actions */}
                {log.action === 'UPDATE' && hasAnyChanges && (
                    <div className="tw-mt-2 tw-bg-gray-50 tw-rounded tw-p-2 tw-border tw-border-gray-100">
                        {/* Journal field diffs */}
                        {journalDiffs.map((diff) => (
                            <ValueDiff key={diff.label} label={diff.label} oldVal={diff.oldVal} newVal={diff.newVal} />
                        ))}

                        {/* Debit/Credit totals */}
                        {oldDebitTotal !== newDebitTotal && (
                            <ValueDiff label="Debit Total" oldVal={`₹${formatCurrency(oldDebitTotal)}`} newVal={`₹${formatCurrency(newDebitTotal)}`} />
                        )}
                        {oldCreditTotal !== newCreditTotal && (
                            <ValueDiff label="Credit Total" oldVal={`₹${formatCurrency(oldCreditTotal)}`} newVal={`₹${formatCurrency(newCreditTotal)}`} />
                        )}

                        {/* Debit lines comparison */}
                        {hasDebitLineChanges && (
                            <LinesCompare label="Debit Lines" oldLines={log.old_values?.debits} newLines={log.new_values?.debits} type="debit" />
                        )}

                        {/* Credit lines comparison */}
                        {hasCreditLineChanges && (
                            <LinesCompare label="Credit Lines" oldLines={log.old_values?.credits} newLines={log.new_values?.credits} type="credit" />
                        )}
                    </div>
                )}

                {/* CREATE action - show initial values */}
                {log.action === 'CREATE' && log.new_values && (
                    <div className="tw-mt-2 tw-bg-emerald-50/50 tw-rounded tw-p-2 tw-border tw-border-emerald-100">
                        <div className="tw-flex tw-items-center tw-gap-3 tw-text-[10px]">
                            <span className="tw-text-emerald-600 tw-font-medium tw-flex tw-items-center tw-gap-0.5">
                                <Icon icon="mdi:arrow-top-right" className="tw-text-[10px]" />
                                Debit: ₹{formatCurrency(newDebitTotal)}
                            </span>
                            <span className="tw-text-red-600 tw-font-medium tw-flex tw-items-center tw-gap-0.5">
                                <Icon icon="mdi:arrow-bottom-left" className="tw-text-[10px]" />
                                Credit: ₹{formatCurrency(newCreditTotal)}
                            </span>
                        </div>
                        {newJournal.remarks && (
                            <p className="tw-text-[10px] tw-text-gray-500 tw-mt-1">
                                Remarks: {newJournal.remarks}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const AuditLogsModal = ({ journalId, onClose }) => {
    const dispatch = useDispatch();
    const { auditLogs, auditLogsLoading, auditLogsPagination } = useSelector(state => state.JournalReducer);
    const societyId = selectedSocietyStorer.getSocietyId();

    useEffect(() => {
        if (journalId && societyId) {
            dispatch(getJournalAuditLogs({ societyId, journalId }));
        }
        return () => {
            dispatch(clearJournalAuditLogs());
        };
    }, [dispatch, journalId, societyId]);

    // Always mounted by the parent; only re-fetch while a journal is open.
    usePageRefresh(() => {
        if (journalId && societyId) dispatch(getJournalAuditLogs({ societyId, journalId }));
    });

    if (!journalId) return null;

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/40" onClick={onClose}>
            <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-w-full tw-max-w-lg tw-mx-4 tw-max-h-[85vh] tw-flex tw-flex-col" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex-shrink-0">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:timeline-clock-outline" className="tw-text-blue-500" />
                        <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800">Audit Logs - Entry #{journalId}</h2>
                    </div>
                    <button onClick={onClose} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-gray-600">
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>

                {/* Body */}
                <div className="tw-px-5 tw-py-4 tw-overflow-y-auto tw-flex-1">
                    {(auditLogsLoading || auditLogs === null) ? (
                        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10">
                            <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-300 tw-border-t-blue-500"></div>
                            <p className="tw-text-xs tw-text-gray-400 tw-mt-2">Loading audit logs...</p>
                        </div>
                    ) : auditLogs.length === 0 ? (
                        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10">
                            <Icon icon="mdi:timeline-clock-outline" className="tw-text-3xl tw-text-gray-300" />
                            <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No audit logs found</p>
                        </div>
                    ) : (
                        <div className="tw-relative tw-pl-6">
                            {auditLogs.map((log, index) => (
                                <AuditLogEntry
                                    key={log.audit_id}
                                    log={log}
                                    isLast={index === auditLogs.length - 1}
                                />
                            ))}
                        </div>
                    )}

                    {/* Pagination info */}
                    {auditLogsPagination && auditLogsPagination.totalCount > 0 && (
                        <div className="tw-text-center tw-mt-3 tw-text-[10px] tw-text-gray-400">
                            Showing {auditLogs?.length || 0} of {auditLogsPagination.totalCount} logs
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-text-right tw-flex-shrink-0">
                    <button onClick={onClose} className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-gray-100 hover:tw-bg-gray-200 tw-ml-auto">
                        <Icon icon="mdi:close-circle-outline" className="tw-text-sm" />
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AuditLogsModal;
